import { addSlideFooter } from '../slide_footer';
import { cleanQuestionTitle } from 'app/pages/Dashboard/pages/Survey/components/Dashboard/utilities/helpers.js';
import { FOOTER_TEXT, TEXT_COLOR, TABLE_BORDER, TABLE_FONT_SIZE, TITLE_TEXT } from '../constants';
import { formatData } from '@utilities/helpers/data_format';

/**
 * Creates multiple continuous sum table slides for CONT_SUM type questions
 * One slide per quota group when compareBy is quota_group, or split tables for overall
 * @param {Object} options
 * @param {String} options.compareBy - Compare by object with value and label properties
 * @param {String} options.displayAs - Display format (PERCENT or COUNT)
 * @param {Object} options.pptx
 * @param {Object} options.questionData - CONT_SUM question data with table structure
 * @param {String} options.questionNumber
 * @param {String} options.questionTitle
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.title
 * @returns {void}
 */
export const createContSumChartSlide = ({
  compareBy,
  displayAs = 'pct',
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  const cleanTitle = cleanQuestionTitle(questionTitle);
  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const titleWithQuestionNumber = `${questionPrefix}${cleanTitle}`;

  // Determine which table to use based on displayAs
  const isDisplayAsPercent = displayAs === 'pct';
  const tableToUse = isDisplayAsPercent ? questionData.tablePct : questionData.tableCount;

  // Check if we're comparing by quota groups
  if (compareBy?.value === 'quota_group') {
    // Create one slide per quota group
    createQuotaGroupSlides({
      pptx,
      reportType,
      reportUrl,
      tableToUse,
      title,
      titleWithQuestionNumber,
    });
  } else {
    // Handle overall and demographic filter comparisons
    createOverallSlides({
      pptx,
      reportType,
      reportUrl,
      tableToUse,
      title,
      titleWithQuestionNumber,
    });
  }
};

/**
 * Creates separate slides for each quota group
 * @param {Object} options
 * @param {Object} options.pptx
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {Object} options.tableToUse
 * @param {String} options.title
 * @param {String} options.titleWithQuestionNumber
 */
const createQuotaGroupSlides = ({
  pptx,
  reportType,
  reportUrl,
  tableToUse,
  title,
  titleWithQuestionNumber,
}) => {
  // Group data by compare_by_breakout (quota group)
  const dataByGroup = {};

  tableToUse.data.forEach((row) => {
    const groupName = row.compare_by_breakout;
    if (!dataByGroup[groupName]) {
      dataByGroup[groupName] = [];
    }
    dataByGroup[groupName].push(row);
  });

  // Create one slide per group
  Object.entries(dataByGroup).forEach(([groupName, groupData]) => {
    const slideTitle = `${titleWithQuestionNumber} - ${groupName}`;

    // Create table structure for this group
    const groupTable = {
      columns: tableToUse.columns.filter((col) => col.accessor !== 'compare_by_breakout'),
      data: groupData,
    };

    // Create slide manually to have better control over the footer
    createCustomTableSlide({
      pptx,
      reportType,
      reportUrl,
      slideTitle,
      table: groupTable,
      title,
    });
  });
};

/**
 * Creates slides for overall data, splitting tables if they're too large
 * @param {Object} options
 * @param {Object} options.pptx
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {Object} options.tableToUse
 * @param {String} options.title
 * @param {String} options.titleWithQuestionNumber
 */
const createOverallSlides = ({
  pptx,
  reportType,
  reportUrl,
  tableToUse,
  title,
  titleWithQuestionNumber,
}) => {
  // Group data by option_value to get unique options
  const optionGroups = {};

  tableToUse.data.forEach((row) => {
    const optionValue = row.option_value;
    if (!optionGroups[optionValue]) {
      optionGroups[optionValue] = [];
    }
    optionGroups[optionValue].push(row);
  });

  const uniqueOptions = Object.keys(optionGroups);
  const maxOptionsPerSlide = 10; // Maximum number of options per slide for readability

  // Split options into chunks
  const optionChunks = [];
  for (let i = 0; i < uniqueOptions.length; i += maxOptionsPerSlide) {
    optionChunks.push(uniqueOptions.slice(i, i + maxOptionsPerSlide));
  }

  // Create one slide per chunk
  optionChunks.forEach((optionChunk, chunkIndex) => {
    const chunkData = [];

    // Collect all data for options in this chunk
    optionChunk.forEach((optionValue) => {
      chunkData.push(...optionGroups[optionValue]);
    });

    const slideTitle =
      optionChunks.length > 1
        ? `${titleWithQuestionNumber} (${chunkIndex + 1}/${optionChunks.length})`
        : titleWithQuestionNumber;

    // Create table structure for this chunk
    const chunkTable = {
      columns: tableToUse.columns,
      data: chunkData,
    };

    // Create slide manually to have better control over the footer
    createCustomTableSlide({
      pptx,
      reportType,
      reportUrl,
      slideTitle,
      table: chunkTable,
      title,
    });
  });
};

/**
 * Processes table data to apply rowspan for identical values in the first column
 * @param {Array} data - Raw table data
 * @param {Array} columns - Column definitions
 * @returns {Array} - Processed table rows with rowspan applied
 */
const processTableDataWithRowspan = (data, columns) => {
  if (!data.length || !columns.length) return [];

  const processedRows = [];
  const firstColumnAccessor = columns[0].accessor;
  let currentGroup = null;
  let groupStartIndex = 0;

  data.forEach((row, rowIndex) => {
    const firstColumnValue = formatData({
      format: columns[0].format,
      value: row[firstColumnAccessor],
    });

    // Check if we're starting a new group
    const isNewGroup = currentGroup !== firstColumnValue;

    if (isNewGroup) {
      // Finalize the previous group by setting rowspan
      if (currentGroup !== null && processedRows.length > groupStartIndex) {
        const groupSize = processedRows.length - groupStartIndex;
        if (groupSize > 1) {
          // Set rowspan for the first cell of the group
          processedRows[groupStartIndex][0].options.rowspan = groupSize;
          // Remove the first cell from subsequent rows in the group
          for (let i = groupStartIndex + 1; i < processedRows.length; i++) {
            processedRows[i].shift();
          }
        }
      }

      // Start new group
      currentGroup = firstColumnValue;
      groupStartIndex = processedRows.length;
    }

    // Create the row data - always include all columns initially
    const rowData = columns.map((col, colIndex) => ({
      options: {
        align: colIndex === 0 ? 'left' : 'right',
        border: TABLE_BORDER,
        color: TEXT_COLOR,
        fontFace: 'Arial',
        fontSize: TABLE_FONT_SIZE,
        valign: 'middle',
      },
      text: formatData({ format: col.format, value: row[col.accessor] }),
    }));

    processedRows.push(rowData);
  });

  // Handle the final group
  if (currentGroup !== null && processedRows.length > groupStartIndex) {
    const groupSize = processedRows.length - groupStartIndex;
    if (groupSize > 1) {
      // Set rowspan for the first cell of the final group
      processedRows[groupStartIndex][0].options.rowspan = groupSize;
      // Remove the first cell from subsequent rows in the final group
      for (let i = groupStartIndex + 1; i < processedRows.length; i++) {
        processedRows[i].shift();
      }
    }
  }

  return processedRows;
};

/**
 * Creates a custom table slide with proper footer including source hyperlink
 * @param {Object} options
 * @param {Object} options.pptx
 * @param {String} options.slideTitle
 * @param {Object} options.table
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.title
 */
const createCustomTableSlide = ({ pptx, reportType, reportUrl, slideTitle, table, title }) => {
  if (!table || !table.columns || !table.data) return;

  const { columns, data } = table;
  const headers = columns.map((col) => col.header || col.Header || col.accessor);
  const rowsPerSlide = 10;

  // Split data into chunks of 10 rows each
  const dataChunks = [];
  for (let i = 0; i < data.length; i += rowsPerSlide) {
    dataChunks.push(data.slice(i, i + rowsPerSlide));
  }

  // Create one slide per chunk
  dataChunks.forEach((chunkData, chunkIndex) => {
    const slide = pptx.addSlide();

    // Add slide title with page indicator if multiple slides
    const finalSlideTitle =
      dataChunks.length > 1 ? `${slideTitle} (${chunkIndex + 1}/${dataChunks.length})` : slideTitle;

    slide.addText(finalSlideTitle, {
      ...TITLE_TEXT,
      fontSize: 14,
    });

    // Prepare table data for this chunk with rowspan grouping
    const processedTableData = processTableDataWithRowspan(chunkData, columns);

    // Calculate column widths
    const TOTAL_TABLE_WIDTH = 9.3;
    const FIRST_COLUMN_RATIO = 0.25; // Give more space to first column for option names
    const columnCount = headers.length;
    const firstColumnWidth = TOTAL_TABLE_WIDTH * FIRST_COLUMN_RATIO;
    const remainingColumnWidth = (TOTAL_TABLE_WIDTH - firstColumnWidth) / (columnCount - 1);
    const columnWidths = [firstColumnWidth, ...Array(columnCount - 1).fill(remainingColumnWidth)];

    // Add table
    slide.addTable(
      [
        // Header row
        headers.map((header, colIndex) => ({
          options: {
            align: colIndex === 0 ? 'left' : 'right',
            bold: true,
            border: TABLE_BORDER,
            color: TEXT_COLOR,
            fontFace: 'Arial',
            fontSize: TABLE_FONT_SIZE,
            valign: 'middle',
          },
          text: header,
        })),
        // Data rows with rowspan
        ...processedTableData,
      ],
      {
        border: TABLE_BORDER,
        colW: columnWidths,
        w: '95%',
        x: 0.3,
        y: 1.0,
      }
    );

    // Add slide footer
    addSlideFooter({ pptx, reportType, reportUrl, slide });

    // Add source hyperlink
    slide.addText(
      [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
      FOOTER_TEXT
    );
  });
};
